/* @jsx h */
import { defineComponent, h } from "vue";
import { useAuth } from "@app/vue/providers/Auth";
import { ResponsiveTable } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { GiftCard, GiftCardStatus } from "@lemmonhq/types";

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  redeemed: "Redeemed",
  expired: "Expired",
  void: "Void",
};

const STATUS_BADGE: Record<string, string> = {
  active: "badge-success",
  redeemed: "badge-neutral",
  expired: "badge-warning",
  void: "badge-error",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-CH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

interface GiftCardsTableProps {
  cards: GiftCard[];
  total: number;
  offset: number;
  limit: number;
  onRowClick: (card: GiftCard) => void;
  onPrev: () => void;
  onNext: () => void;
}

export const GiftCardsTable = defineComponent<GiftCardsTableProps>({
  name: "GiftCardsTable",
  props: [
    "cards",
    "total",
    "offset",
    "limit",
    "onRowClick",
    "onPrev",
    "onNext",
  ],
  setup(props) {
    const auth = useAuth();
    const columns: ResponsiveTableColumn[] = [
      {
        key: "code",
        label: "Code",
        render: (card: GiftCard) => (
          <span class="font-mono text-xs">{card.code.substring(0, 20)}…</span>
        ),
      },
      {
        key: "initial_value",
        label: "Value",
        render: (card: GiftCard) => (
          <span>
            {auth.user.value?.restaurant.currency || "CHF"}{" "}
            {Number(card.initial_value).toFixed(2)}
          </span>
        ),
      },
      {
        key: "current_balance",
        label: "Balance",
        render: (card: GiftCard) => (
          <span class="font-semibold text-success">
            {auth.user.value?.restaurant.currency || "CHF"}{" "}
            {Number(card.current_balance).toFixed(2)}
          </span>
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (card: GiftCard) => (
          <span
            class={`badge badge-sm ${STATUS_BADGE[card.status] || "badge-neutral"}`}
          >
            {STATUS_LABELS[card.status] || card.status}
          </span>
        ),
      },
      {
        key: "created_at",
        label: "Issued",
        render: (card: GiftCard) => (
          <span class="text-sm">{formatDate(card.created_at)}</span>
        ),
      },
    ];

    return () => (
      <div class="p-4">
        <ResponsiveTable
          columns={columns}
          rows={props.cards}
          rowKey={(card: GiftCard) => card.id}
          onRowClick={props.onRowClick}
        />
        {props.total > props.limit && (
          <div class="flex justify-center items-center gap-3 mt-4">
            <button
              class="btn btn-sm btn-ghost"
              disabled={props.offset === 0}
              onClick={props.onPrev}
            >
              Prev
            </button>
            <span class="text-sm text-base-content/60">
              {props.offset + 1}–
              {Math.min(props.offset + props.limit, props.total)} of{" "}
              {props.total}
            </span>
            <button
              class="btn btn-sm btn-ghost"
              disabled={props.offset + props.limit >= props.total}
              onClick={props.onNext}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  },
});
